// Kyte amenity resource module. Fare amenities ship as free-text in
// `fare.description[]`. Some carriers prefix each line with INC/CHA/NOF;
// others (Ryanair, Wizz) ship plain phrases where chargeability is inferred
// via CHARGEABLE_HINTS. Contract: EVERY description line yields exactly one
// amenity entry; the `code` is derived from the description itself (initials,
// KYTE_-prefixed) so identical phrases dedupe and distinct ones keep their
// rows in the comparison table. Output shape matches Amadeus plus a
// Kyte-only `tooltip`: `{ code, description, tooltip, isChargeable,
// amenityType }`.

/**
 * Curated label/icon/tooltip dictionary for high-frequency Kyte phrases.
 * Codes use two conventions:
 *   - "KYTE_<initials>" for description-derived stable IDs (default from
 *     `derive_kyte_code`).
 *   - "KYTE_<ANCILLARY>" for callers that know the ancillary category up
 *     front; the tokenizer never emits these.
 */
export const KYTE_AMENITIES = {
  // Ryanair / Wizz baggage phrases
  KYTE_1SB: {
    txt: 'Borsa piccola sotto al sedile',
    icon: 'backpack',
    tooltip: 'Deve entrare sotto il sedile davanti a te',
  },
  KYTE_2CIB: { txt: 'Bagaglio in stiva 20 kg', icon: 'luggage', tooltip: '' },
  KYTE_1OLB: {
    txt: 'Bagaglio cabina 10 kg',
    icon: 'trolley',
    tooltip: 'Da riporre nel vano sopra la testa',
  },
  KYTE_PB: { txt: 'Imbarco prioritario', icon: 'star', tooltip: '' },

  // Seat selection
  KYTE_ARS: {
    txt: 'Posto a sedere assegnato',
    icon: 'seat_choice',
    tooltip: '',
  },
  KYTE_RS: {
    txt: 'Posto a sedere assegnato',
    icon: 'seat_choice',
    tooltip: '',
  },

  // Refund / change
  KYTE_NR: { txt: 'Non rimborsabile', icon: 'wallet', tooltip: '' },
  KYTE_R: { txt: 'Rimborsabile', icon: 'wallet', tooltip: '' },
  KYTE_CFAFORW: {
    txt: 'Modificabile a pagamento',
    icon: 'pen',
    tooltip: 'Modifica disponibile sul sito Ryanair con costo aggiuntivo',
  },
  KYTE_C: { txt: 'Modificabile', icon: 'pen', tooltip: '' },

  // Generic ancillary-style codes — emitted only by hand-built fixtures and
  // the upsell catalog; the tokenizer keeps initials-style codes.
  KYTE_HAND_BAG: { txt: 'Borsa piccola', icon: 'backpack', tooltip: '' },
  KYTE_CABIN_BAG: { txt: 'Bagaglio in cabina', icon: 'trolley', tooltip: '' },
  KYTE_CHECKED_BAG: { txt: 'Bagaglio in stiva', icon: 'luggage', tooltip: '' },
  KYTE_SPORTS: {
    txt: 'Attrezzatura sportiva',
    icon: 'check_circle',
    tooltip: '',
  },
  KYTE_SEAT: { txt: 'Selezione del posto', icon: 'seat_choice', tooltip: '' },
  KYTE_DISABILITY: {
    txt: 'Assistenza disabilità',
    icon: 'accessibility',
    tooltip: '',
  },
};

/**
 * Upstream fare/bundle phrase → Italian copy, keyed `<CARRIER>:<hash>`
 * where the hash is the verbatim description lowercased with all
 * whitespace removed (see `kyte_amenity_translation_key`). Keys are
 * stored pre-hashed so minor upstream whitespace/casing churn never
 * breaks the map; punctuation still matters (note the GO bundle ships
 * the check-in phrase without the trailing dot — both variants listed).
 * W6 entries mirror the official wizzair.com it-IT fare cards: `txt` is
 * the card's main line verbatim, `tooltip` carries the sizes / secondary
 * copy. Volatile specifics (fees, hour windows) stay out of tooltips.
 */
export const KYTE_AMENITY_TRANSLATIONS = {
  'W6:onesmallcarry-onbag(40x30x20cm)': {
    txt: 'Borsa sotto al sedile gratuita',
    tooltip: 'Oggetto personale 40x30x20 cm sotto al sedile',
  },
  'W6:onlinecheck-in': { txt: 'Check-in online gratuito', tooltip: '' },
  'W6:+1carry-onbag40x30x20cm(placedundertheseat)': {
    txt: 'Borsa sotto al sedile gratuita',
    tooltip: '40 x 30 x 20 cm',
  },
  'W6:trolleybag55x40x23cmguaranteedincabinarea(exceptforoperationalreasons)': {
    txt: 'Trolley da 10 kg a bordo',
    tooltip: '55 x 40 x 23 cm · garantito in cabina salvo motivi operativi',
  },
  'W6:32kgchecked-inbagincluded.additionalbagsofthesameweightcanbepurchased.': {
    txt: 'Bagaglio da stiva 32 kg',
    tooltip: 'Possibilità di acquistare bagagli aggiuntivi dello stesso peso',
  },
  'W6:20kgchecked-inbagincluded.upgradingthebaggageweightreplacestheoriginalallowance,weightupgradesarechargedonapricedifferencebasis.additionalbagsofthesameweightcanbepurchased.':
    {
      txt: 'Bagaglio da stiva da 20 kg',
      tooltip:
        "L'aumento di peso sostituisce la franchigia originale e viene addebitata la sola differenza di prezzo. Possibilità di acquistare bagagli aggiuntivi dello stesso peso",
    },
  'W6:premiumseatselectionwiththeextralegroomseats': {
    txt: 'Scelta del posto premium',
    tooltip: 'Inclusi posti in prima fila e più spaziosi',
  },
  'W6:seatselection(1stto4throwandextralegroomseatsareexcluded)': {
    txt: 'Scelta del posto normale',
    tooltip: 'Escluse alcune file e posti più spaziosi',
  },
  'W6:wizzflexfeaturethatallowsyoutochangeyourflightdateifneeded,withoutanyextracharge':
    {
      txt: 'Cambio di volo online senza penali',
      tooltip: 'Potrebbe essere dovuta la differenza di tariffa',
    },
  'W6:refundtowizzaccount': {
    txt: 'Rimborso sul conto WIZZ',
    tooltip: 'Rimborso soggetto a termini e condizioni',
  },
  'W6:priorityboarding': {
    txt: 'Imbarco prioritario',
    tooltip: "Sii tra i primi a salire sull'aereo",
  },
  'W6:prioritycheck-in': { txt: 'Check-in prioritario', tooltip: '' },
  'W6:autocheck-in': { txt: 'Check-in automatico', tooltip: '' },
  'W6:freeairport&onlinecheck-inupto30daysbeforeflightdeparture.': {
    txt: 'Check-in online e in aeroporto gratuito',
    tooltip: '',
  },
  'W6:freeairport&onlinecheck-inupto30daysbeforeflightdeparture': {
    txt: 'Check-in online e in aeroporto gratuito',
    tooltip: '',
  },
  'W6:onlinecheck-inupto30daysbeforeflightdeparture': {
    txt: 'Check-in online gratuito',
    tooltip: '',
  },
};

/**
 * Build the `<CARRIER>:<hash>` lookup key for an amenity translation. The
 * hash is the description lowercased with all whitespace removed, so a
 * translation only applies on a complete phrase match — partial matches
 * stay untranslated rather than guessed.
 * @param {string} carrier marketing carrier code, e.g. "W6"
 * @param {string} description verbatim upstream phrase
 * @returns {string}
 */
export const kyte_amenity_translation_key = (carrier, description) => {
  const code = String(carrier == null ? '' : carrier).toUpperCase();
  const hash = String(description == null ? '' : description)
    .toLowerCase()
    .replace(/\s+/g, '');
  return `${code}:${hash}`;
};

/**
 * Look up the `{ txt, tooltip }` translation entry for an upstream amenity
 * phrase; null when no complete match exists in the map.
 * @param {string} carrier
 * @param {string} description
 * @returns {{txt: string, tooltip: string}|null}
 */
export const get_kyte_amenity_translation = (carrier, description) =>
  KYTE_AMENITY_TRANSLATIONS[
    kyte_amenity_translation_key(carrier, description)
  ] || null;

// "INC - CABIN BAG …", "CHA - 1ST BAG …", "NOF - LOUNGE ACCESS". Accepts ":"
// and em-dash variants for robustness against BE quirks.
const parse_status_prefix = (source) => {
  const match = source.match(/^\s*(INC|CHA|NOF)\s*[-:–—]\s*(.*)$/i);
  if (!match) {
    return null;
  }
  return { status: match[1].toUpperCase(), remainder: match[2] };
};

// Heuristic for descriptions without an INC/CHA/NOF prefix; anything that
// smells like a fee or a refusal counts as not-included.
const CHARGEABLE_HINTS =
  /\bfor a fee\b|\bfee\b|\bcharge\b|\bextra\s+(cost|fee|charge)\b|\bnon[-\s]?refundable\b|\bno\s+refund\b|\bnot\s+(included|offered|allowed|available)\b|\bmust be (paid|added)\b/i;

// Order matters: more-specific patterns (handbag / personal-item) sit above
// broader siblings (cabin / overhead) so the right `amenityType` wins.
const TYPE_PATTERNS = [
  {
    match:
      /\bhandbag\b|\bhand bag\b|\bpersonal item\b|\bsmall bag\b|\bunder the seat\b/i,
    amenityType: 'BAGGAGE',
  },
  {
    match: /\bcabin bag\b|\bcarry-?on\b|\boverhead\b|\blocker\b/i,
    amenityType: 'BAGGAGE',
  },
  {
    match:
      /\b\d+(st|nd|rd|th)?\s+bag\b|\bchecked bag\b|\bcheck-?in bag\b|\bbag max\b|\bbag up to\b|\bstowed\b|\bstiva\b|\bhold bag\b|\b\d+\s?kg\s+bag\b/i,
    amenityType: 'BAGGAGE',
  },
  {
    match:
      /\blie[-\s]?flat|\bdirect aisle\b|\bseat (choice|selection|assignment)|\bstandard seat|\bfree seat|\bpaid seat|\breserved seat|\bseat\b/i,
    amenityType: 'PRE_RESERVED_SEAT',
  },
  {
    match:
      /\bchange (before|after)|\bdate change|\bflight change|\bchanges?\b|\brefund (before|after)|\brefund\b|\brefundable\b/i,
    amenityType: 'BRANDED_FARES',
  },
  { match: /\blounge\b/i, amenityType: 'LOUNGE' },
  {
    match:
      /\bsky priority|\bpriority (security|check[-\s]?in|boarding|baggage)|\bfast track|\bdedicated check|\bpriority boarding\b/i,
    amenityType: 'TRAVEL_SERVICES',
  },
  {
    match:
      /\bmiles?\b|\bmileage\b|\bpoints?\b|\bloyalty\b|\bvolare\b|\bflying blue\b|\bskymiles\b|\bavios\b/i,
    amenityType: 'TRAVEL_SERVICES',
  },
  {
    match:
      /\bmeal\b|\bdining\b|\bcatering\b|\bfood\s+(and|&)\s+bar|\bfood\s+service|\bbar service/i,
    amenityType: 'MEAL',
  },
  { match: /\bwi-?fi\b/i, amenityType: 'ENTERTAINMENT' },
];

const infer_amenity_type = (text) => {
  const hit = TYPE_PATTERNS.find((t) => t.match.test(text));
  return hit ? hit.amenityType : 'MISC';
};

/**
 * Derive a stable KYTE_<initials> code from a description. Strips parens,
 * lowercases, tokenizes on non-alphanumeric, takes first character of each
 * token. Examples: "1 small bag (...)" → "KYTE_1SB"; "Non refundable" →
 * "KYTE_NR". Identical phrases produce identical codes so cross-fare dedupe
 * works.
 * @param {string} text
 * @returns {string}
 */
export const derive_kyte_code = (text) => {
  const cleaned = String(text == null ? '' : text)
    .replace(/\([^)]*\)/g, ' ')
    .toLowerCase();
  const tokens = cleaned.split(/[^a-z0-9]+/).filter((t) => t.length > 0);
  const initials = tokens
    .map((t) => t.charAt(0))
    .join('')
    .toUpperCase();
  return initials ? `KYTE_${initials}` : 'KYTE_OTHER';
};

/**
 * Convert one fare-description line into a single amenity entry. Dedup
 * across lines is the caller's job (see kyte_amenities_for_fare). Returns
 * an empty array only when the description is empty. Code, amenityType and
 * chargeability derive from the English source text; only the displayed
 * description / tooltip come from the carrier translation map when the
 * phrase matches completely.
 * @param {string} text
 * @param {string} carrier marketing carrier code, e.g. "W6"
 * @returns {Array}
 */
export const kyte_amenities_from_description = (text, carrier) => {
  const source = String(text == null ? '' : text);
  const parsed = parse_status_prefix(source);
  const haystack = parsed ? parsed.remainder.trim() : source.trim();
  if (!haystack) {
    return [];
  }
  const isChargeable = parsed
    ? parsed.status !== 'INC'
    : CHARGEABLE_HINTS.test(haystack);
  const { txt: description = haystack, tooltip = '' } =
    get_kyte_amenity_translation(carrier, haystack) || {};
  return [
    {
      code: derive_kyte_code(haystack),
      description,
      tooltip,
      amenityType: infer_amenity_type(haystack),
      isChargeable,
    },
  ];
};

/**
 * Build the amenity list for one matched Kyte fare. Dedupe key includes
 * the description string so distinct phrases that happen to derive the
 * same code ("Cabin bag (10kg)" vs "Cabin bag (23kg)" → both KYTE_CB)
 * keep separate rows.
 * @param {object} fare
 * @param {string} carrier marketing carrier code, e.g. "W6"
 * @returns {Array}
 */
export const kyte_amenities_for_fare = (fare, carrier) => {
  if (!fare) {
    return [];
  }
  const { description: descriptions = [] } = fare || {};
  if (!Array.isArray(descriptions)) {
    return [];
  }
  const seen = {};
  return descriptions
    .filter((text) => text && !String(text).startsWith('-'))
    .reduce((amenities, text) => {
      const current = kyte_amenities_from_description(text, carrier);
      return [
        ...amenities,
        ...current.reduce((out, amenity) => {
          const key = `${amenity.code}:${amenity.isChargeable ? 'x' : 'i'}:${amenity.description}`;
          if (seen[key]) {
            return out;
          }
          seen[key] = true;
          return [...out, amenity];
        }, []),
      ];
    }, []);
};
